import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudApiOptions, GenResp } from "@template-ui/services/crud-api.service";
import { AppBaseService } from "@app/shared/services/app-base.service";
import { map, Observable } from "rxjs";
import {
  FeatureFlagDefinitionCreateRequest,
  FeatureFlagDefinitionDetail,
  FeatureFlagDefinitionSummary,
  FeatureFlagDefinitionUpdateRequest,
  FeatureFlagRollout,
  FeatureFlagRolloutUpsertRequest,
  mapFeatureFlagAdminError,
} from "./platform-feature-flag-admin.models";

const requestHeaders = new HttpHeaders({
  "Content-Type": "application/json",
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

const featureFlagAdminApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  postOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  putOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformFeatureFlagAdminService extends AppBaseService {
  listDefinitions(): Observable<FeatureFlagDefinitionSummary[]> {
    return this.crudService.getbyUrl<FeatureFlagDefinitionSummary[]>(
      this.apis.core.manual.getPlatformConfigurationFeatureFlags(),
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  getDefinition(flagCode: string): Observable<FeatureFlagDefinitionDetail> {
    return this.crudService.getbyUrl<FeatureFlagDefinitionDetail>(
      this.apis.core.manual.getPlatformConfigurationFeatureFlag(flagCode),
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  createDefinition(request: FeatureFlagDefinitionCreateRequest): Observable<FeatureFlagDefinitionDetail> {
    return this.crudService.postByUrl<FeatureFlagDefinitionDetail>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlags(),
      request,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  updateDefinition(
    flagCode: string,
    request: FeatureFlagDefinitionUpdateRequest,
  ): Observable<FeatureFlagDefinitionDetail> {
    return this.crudService.putByUrl<FeatureFlagDefinitionDetail>(
      this.apis.core.manual.putPlatformConfigurationFeatureFlag(flagCode),
      request,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  activateDefinition(flagCode: string): Observable<FeatureFlagDefinitionDetail> {
    return this.crudService.postByUrl<FeatureFlagDefinitionDetail>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlagActivate(flagCode),
      null,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  deactivateDefinition(flagCode: string): Observable<FeatureFlagDefinitionDetail> {
    return this.crudService.postByUrl<FeatureFlagDefinitionDetail>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlagDeactivate(flagCode),
      null,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  createRollout(flagCode: string, request: FeatureFlagRolloutUpsertRequest): Observable<FeatureFlagRollout> {
    return this.crudService.postByUrl<FeatureFlagRollout>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlagRollouts(flagCode),
      request,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  updateRollout(
    flagCode: string,
    rolloutId: number,
    request: FeatureFlagRolloutUpsertRequest,
  ): Observable<FeatureFlagRollout> {
    return this.crudService.putByUrl<FeatureFlagRollout>(
      this.apis.core.manual.putPlatformConfigurationFeatureFlagRollout(flagCode, rolloutId),
      request,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  publishRollout(flagCode: string, rolloutId: number): Observable<FeatureFlagRollout> {
    return this.crudService.postByUrl<FeatureFlagRollout>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlagRolloutPublish(flagCode, rolloutId),
      null,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  rollbackRollout(flagCode: string, rolloutId: number): Observable<FeatureFlagRollout> {
    return this.crudService.postByUrl<FeatureFlagRollout>(
      this.apis.core.manual.postPlatformConfigurationFeatureFlagRolloutRollback(flagCode, rolloutId),
      null,
      featureFlagAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }
}

export { mapFeatureFlagAdminError };

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}
